import {
	validateLookupResponse,
	validateTwGamingLookupsResponse,
} from "@utils/lookups/validators";
import {
	sortAndPrepareEstimatedGamingAmounts,
	sortByNameLocale,
} from "@utils/lookups/mapper";
import { getIsoFromSupportedLanguages } from "@utils";
import { LookupItem } from "@data-types";
import {
	AnnualIncomeSpanDataType,
	CountryDataType,
	EstimatedGamingAmountDataType,
	GamingLimitDataType,
	LanguageDataType,
	OccupationDataType,
	OccupationGroupDataType,
	RegionDataType,
} from "@data-types/membership/registration";
import {
	PlatformAnnualIncomeSpansApiService,
	PlatformCountryApiService,
	PlatformCountryRegionsApiService,
	PlatformEstimatedGamingAmountsApiService,
	PlatformLanguagesApiService,
	PlatformOccupationGroupsApiService,
	PlatformOccupationsApiService,
	PlatformTitlesApiService,
} from "@api-services/platform";
import { TwGamingLimitsApiService } from "@api-services/agency";
import { logger } from "@utils/logger/logger";

class RegistrationLookupService {
	static async getTitles(): Promise<LookupItem[] | []> {
		try {
			const response = await PlatformTitlesApiService.findTitles();

			const isValid = validateLookupResponse(response, "getTitles");

			return isValid ? response.item : [];
		} catch (error) {
			logger.logError(error);
			return [];
		}
	}

	static async getAnnualIncomeSpans(): Promise<AnnualIncomeSpanDataType[]> {
		try {
			const response =
				await PlatformAnnualIncomeSpansApiService.findAnnualIncomeSpans();

			const isValid = validateLookupResponse(
				response,
				"getAnnualIncomeSpans"
			);

			return isValid ? response.item : [];
		} catch (error) {
			logger.logError(error);
			return [];
		}
	}

	static async getCountries(): Promise<CountryDataType[]> {
		try {
			const response = await PlatformCountryApiService.findCountries();

			const isValid = validateLookupResponse(response, "getCountries");

			if (isValid) {
				const sorted = sortByNameLocale(response.item);
				return sorted;
			}

			return [];
		} catch (error) {
			logger.logError(error);
			return [];
		}
	}

	static async getLanguages(): Promise<LanguageDataType[]> {
		try {
			const response = await PlatformLanguagesApiService.findLanguages(
				getIsoFromSupportedLanguages()
			);

			const isValid = validateLookupResponse(response, "getLanguages");

			if (isValid) {
				const sorted = sortByNameLocale(response.item);
				return sorted;
			}

			return [];
		} catch (error) {
			logger.logError(error);
			return [];
		}
	}

	static async getCountryRegions(): Promise<RegionDataType[]> {
		try {
			const response =
				await PlatformCountryRegionsApiService.findCountryRegions();

			const isValid = validateLookupResponse(
				response,
				"getCountryRegions"
			);

			if (isValid) {
				const sorted = sortByNameLocale(response.item);
				return sorted;
			}

			return [];
		} catch (error) {
			logger.logError(error);
			return [];
		}
	}

	static async getGamingLimits(): Promise<GamingLimitDataType | null> {
		try {
			const response = await TwGamingLimitsApiService.findGamingLimits();

			const isValid = validateTwGamingLookupsResponse(response);

			return isValid ? response : null;
		} catch (error) {
			logger.logError(error);
			return null;
		}
	}

	static async getOccupations(): Promise<OccupationDataType[]> {
		try {
			const response =
				await PlatformOccupationsApiService.findOccupations();

			const isValid = validateLookupResponse(response, "getOccupations");

			if (isValid) {
				const sorted = sortByNameLocale(response.item);
				return sorted;
			}

			return [];
		} catch (error) {
			logger.logError(error);
			return [];
		}
	}

	static async getOccupationGroups(): Promise<OccupationGroupDataType[]> {
		try {
			const response =
				await PlatformOccupationGroupsApiService.findOccupationGroups();

			const isValid = validateLookupResponse(
				response,
				"getOccupationGroups"
			);

			if (isValid) {
				const sorted = sortByNameLocale(response.item);
				return sorted;
			}

			return [];
		} catch (error) {
			logger.logError(error);
			return [];
		}
	}

	static async getEstimatedGamingAmounts(): Promise<
		EstimatedGamingAmountDataType[]
	> {
		try {
			const response =
				await PlatformEstimatedGamingAmountsApiService.findEstimatedGamingAmount();

			const isValid = validateLookupResponse(
				response,
				"getEstimatedGamingAmounts"
			);

			if (isValid) {
				const sorted = sortAndPrepareEstimatedGamingAmounts(
					response.item
				);
				return sorted;
			}

			return [];
		} catch (error) {
			logger.logError(error);
			return [];
		}
	}
}

export default RegistrationLookupService;
